import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import config from '../../config';
import { getAdminUploadHeaders } from '../../utils/apiAuth';

const PUBLIC_FOLDER = 'public_folder';
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const PublicFolderImageField = ({
  label,
  value = '',
  onChange,
  placeholder = 'Enter image URL or upload to S3',
  helperText
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [filename, setFilename] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imageError, setImageError] = useState(false);

  const apiBaseUrl = config.getApiUrl();

  useEffect(() => {
    setImageError(false);
  }, [value, previewUrl]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile]);

  const emitChange = (url) => {
    if (onChange) {
      onChange(url);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return;
    }
    setSelectedFile(file);
    setFilename(file.name);
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    if (!filename.trim()) {
      setError('Please enter a filename');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('filename', filename.trim());
      formData.append('folder', PUBLIC_FOLDER);

      const response = await fetch(`${apiBaseUrl}/api/s3-upload`, {
        method: 'POST',
        headers: getAdminUploadHeaders(),
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const uploadedUrl = data.data?.url || '';
        emitChange(uploadedUrl);
        setSuccess(data.message || 'Upload successful');
        setSelectedFile(null);
        setFilename('');
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Cannot connect to server. Please ensure the backend is running on ' + apiBaseUrl);
      } else {
        setError('Failed to upload file. Please try again. Error: ' + err.message);
      }
    } finally {
      setUploading(false);
    }
  };

  const thumbnailSrc = previewUrl || value;
  const showThumbnail = Boolean(thumbnailSrc) && !imageError;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            flexShrink: 0,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {showThumbnail ? (
            <Box
              component="img"
              src={thumbnailSrc}
              alt={label || 'Uploaded image'}
              onError={() => setImageError(true)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <ImageNotSupportedIcon sx={{ color: 'grey.500' }} />
          )}
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            label={label}
            value={value}
            onChange={(e) => emitChange(e.target.value)}
            placeholder={placeholder}
            helperText={helperText}
            sx={{ mb: 1.5 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              Select Image
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleUpload}
              disabled={!selectedFile || !filename.trim() || uploading}
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            >
              {uploading ? 'Uploading...' : 'Upload to S3'}
            </Button>
          </Box>
        </Box>
      </Box>

      {selectedFile && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="banner.jpg"
            helperText="Final filename in public_folder. Include the file extension. Same name overwrites."
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default PublicFolderImageField;
